/**
 * Field Registration Service
 *
 * Orchestrates calls to existing services (stcBin, stcBinType, itemBinSize, items)
 * to support the field registration module. No new backend endpoints needed.
 */

import { stcBinService } from './stcBinService';
import { stcBinTypeService, StcBinTypeDto } from './stcBinTypeService';
import { stcBinSizeService } from './stcBinSizeService';
import { stcBinGrpService, StcBinGrpDto } from './stcBinGrpService';
import { stcMovementTypeService, StcMovementTypeDto } from './stcMovementTypeService';
import { itemBinSizeService } from './itemBinSizeService';
import { itemsService } from './itemsService';
import { AuthService } from './authService';
import { StcBinDto, UpdateStcBinRequest } from '../types/stcBin';
import { StcBinSizeDto } from '../types/stcBinSize';
import { ItemBinSizeDto, CreateItemBinSizeRequest, UpdateItemBinSizeRequest } from '../types/itemBinSize';
import { ItemsDto, UpdateItemsRequest } from '../types/items';
import {
  FieldRegistrationLocation,
  FieldRegistrationItem,
  BinFieldOverrides,
  LocationCompletionStatus,
  FieldRegistrationSummary,
  EditableBinProperties,
  EditableItemCapacity,
} from '../types/fieldRegistration';
import { ItemBinsDto } from '../types/dataInspection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** Threshold: MaxQuantity at or above this is considered "not configured" */
const UNCONFIGURED_MAX_QTY = 999999;

class FieldRegistrationService {

  // ── Reference Data (cached per session) ──────────────────────────

  private binTypesCache: Map<string, StcBinTypeDto[]> = new Map();
  private binSizesCache: Map<string, StcBinSizeDto[]> = new Map();
  private binGrpsCache: Map<string, StcBinGrpDto[]> = new Map();
  private movementTypesCache: Map<string, StcMovementTypeDto[]> = new Map();

  /**
   * Get all bin types for company (cached)
   */
  async getBinTypes(companyNo: string): Promise<StcBinTypeDto[]> {
    if (this.binTypesCache.has(companyNo)) {
      return this.binTypesCache.get(companyNo)!;
    }
    const types = await stcBinTypeService.getByCompanyNo(companyNo);
    this.binTypesCache.set(companyNo, types);
    return types;
  }

  /**
   * Get a specific bin type
   */
  async getBinType(companyNo: string, binTypeNo: number): Promise<StcBinTypeDto | null> {
    const types = await this.getBinTypes(companyNo);
    return types.find(t => t.binTypeNo === binTypeNo) || null;
  }

  /**
   * Get all bin sizes for company (cached)
   */
  async getBinSizes(companyNo: string): Promise<StcBinSizeDto[]> {
    if (this.binSizesCache.has(companyNo)) {
      return this.binSizesCache.get(companyNo)!;
    }
    const sizes = await stcBinSizeService.getByCompanyNo(companyNo);
    this.binSizesCache.set(companyNo, sizes);
    return sizes;
  }

  /**
   * Get a specific bin size
   */
  async getBinSize(companyNo: string, binSizeNo: number): Promise<StcBinSizeDto | null> {
    const sizes = await this.getBinSizes(companyNo);
    return sizes.find(s => s.binSizeNo === binSizeNo) || null;
  }

  /**
   * Get all bin groups for company (cached)
   */
  async getBinGroups(companyNo: string): Promise<StcBinGrpDto[]> {
    if (this.binGrpsCache.has(companyNo)) {
      return this.binGrpsCache.get(companyNo)!;
    }
    const groups = await stcBinGrpService.getByCompanyNo(companyNo);
    this.binGrpsCache.set(companyNo, groups);
    return groups;
  }

  /**
   * Get all movement types for company (cached)
   */
  async getMovementTypes(companyNo: string): Promise<StcMovementTypeDto[]> {
    if (this.movementTypesCache.has(companyNo)) {
      return this.movementTypesCache.get(companyNo)!;
    }
    const types = await stcMovementTypeService.getByCompanyNo(companyNo);
    this.movementTypesCache.set(companyNo, types);
    return types;
  }

  /** Clear cached reference data */
  clearCache(): void {
    this.binTypesCache.clear();
    this.binSizesCache.clear();
    this.binGrpsCache.clear();
    this.movementTypesCache.clear();
  }

  // ── Location Operations ──────────────────────────────────────────

  /**
   * Load all bins for a warehouse, enriched with type/size info
   */
  async loadLocations(companyNo: string, warehouseID: string): Promise<StcBinDto[]> {
    return stcBinService.getByCompanyAndWarehouse(companyNo, warehouseID);
  }

  /**
   * Load a single bin by exact bin code
   */
  async loadLocationByBinCode(companyNo: string, binCode: string): Promise<StcBinDto | null> {
    return stcBinService.getByExactBinCode(companyNo, binCode);
  }

  /**
   * Search bins by search term
   */
  async searchLocations(companyNo: string, searchTerm: string): Promise<StcBinDto[]> {
    return stcBinService.search(companyNo, searchTerm);
  }

  /**
   * Build a fully enriched FieldRegistrationLocation for a bin
   */
  async buildLocationDetail(companyNo: string, bin: StcBinDto): Promise<FieldRegistrationLocation> {
    const startTime = performance.now();

    // Load type, size, and items in parallel
    const [binType, binSize, itemBins] = await Promise.all([
      this.getBinType(companyNo, bin.binTypeNo),
      this.getBinSize(companyNo, bin.binSizeNo),
      this.getItemsAtLocation(bin.binCode),
    ]);

    // For each item at this location, load ItemBinSize config
    const items = await this.enrichItemsWithCapacity(companyNo, itemBins, bin.binSizeNo);

    // Calculate overrides
    const overrides = this.calculateOverrides(bin, binType);

    // Calculate completion status
    const completionStatus = this.calculateCompletionStatus(items, overrides);

    const elapsed = Math.round(performance.now() - startTime);
    console.log(`[FieldRegistration] Built location detail for ${bin.binCode} in ${elapsed}ms (${items.length} items)`);

    return {
      bin,
      binType,
      binSize,
      items,
      overrides,
      completionStatus,
    };
  }

  // ── Item Operations ──────────────────────────────────────────────

  /**
   * Get items at a location via data-inspection endpoint (ItemBins by BinCode)
   */
  private async getItemsAtLocation(binCode: string): Promise<Array<{ itemNo: string; quantity: number }>> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/data-inspection/itembins/by-bin/${encodeURIComponent(binCode)}`
      );
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error(`Failed to get items at location: ${response.status}`);
      }
      const itemBins: ItemBinsDto[] = await response.json();
      return itemBins.map((ib) => ({
        itemNo: ib.itemNo,
        quantity: ib.quantity || 0,
      }));
    } catch (error) {
      console.error(`[FieldRegistration] Error loading items for ${binCode}:`, error);
      return [];
    }
  }

  /**
   * Enrich item list with descriptions and capacity config
   */
  private async enrichItemsWithCapacity(
    companyNo: string,
    itemBins: Array<{ itemNo: string; quantity: number }>,
    binSizeNo: number
  ): Promise<FieldRegistrationItem[]> {
    if (itemBins.length === 0) return [];

    // Load all ItemBinSize and Item details in parallel
    const enrichPromises = itemBins.map(async (ib) => {
      const [itemBinSize, itemDetail] = await Promise.all([
        itemBinSizeService.getByCompanyItemAndBinSize(companyNo, ib.itemNo, binSizeNo).catch(() => null),
        itemsService.getItemByCompanyAndItemNo(companyNo, ib.itemNo).catch(() => null),
      ]);

      const capacityConfigured = itemBinSize !== null && itemBinSize.maxQuantity < UNCONFIGURED_MAX_QTY;

      return {
        itemNo: ib.itemNo,
        description1: itemDetail?.description1 || ib.itemNo,
        itemBarCode: itemDetail?.itemBarCode,
        currentQuantity: ib.quantity,
        itemBinSize,
        capacityConfigured,
        stdBinCode: itemDetail?.stdBinCode || undefined,
        stdLocationName: itemDetail?.stdLocationName || undefined,
      } as FieldRegistrationItem;
    });

    return Promise.all(enrichPromises);
  }

  /**
   * Search for an item by barcode
   */
  async searchItemByBarcode(barcode: string): Promise<ItemsDto[]> {
    return itemsService.searchByBarcode(barcode);
  }

  /**
   * Get item by company and item number
   */
  async getItem(companyNo: string, itemNo: string): Promise<ItemsDto | null> {
    try {
      return await itemsService.getItemByCompanyAndItemNo(companyNo, itemNo);
    } catch {
      return null;
    }
  }

  // ── Update Operations ────────────────────────────────────────────

  /**
   * Update bin properties (field registration editable subset)
   */
  async updateBinProperties(
    companyNo: string,
    warehouseID: string,
    binCode: string,
    currentBin: StcBinDto,
    properties: EditableBinProperties
  ): Promise<StcBinDto> {
    const request: UpdateStcBinRequest = {
      binSizeNo: currentBin.binSizeNo,
      binTypeNo: currentBin.binTypeNo,
      // Editable fields (original)
      pickPriorityNo: properties.pickPriorityNo ?? currentBin.pickPriorityNo,
      putAwayPriorityNo: properties.putAwayPriorityNo ?? currentBin.putAwayPriorityNo,
      sharedBinYesNo: properties.sharedBinYesNo ?? currentBin.sharedBinYesNo,
      autoReplenishment: properties.autoReplenishment ?? currentBin.autoReplenishment,
      replenishMinLevel: properties.replenishMinLevel ?? currentBin.replenishMinLevel,
      replenishMaxLevel: properties.replenishMaxLevel ?? currentBin.replenishMaxLevel,
      pickableYesNo: properties.pickableYesNo ?? currentBin.pickableYesNo,
      reservationAllowed: properties.reservationAllowed ?? currentBin.reservationAllowed,
      // New editable fields
      itemNo: properties.itemNo !== undefined ? (properties.itemNo || undefined) : currentBin.itemNo,
      sharedBinMaxQuantity: properties.sharedBinMaxQuantity ?? currentBin.sharedBinMaxQuantity,
      movementTypeNo: properties.movementTypeNo !== undefined ? (properties.movementTypeNo || undefined) : currentBin.movementTypeNo,
      customSortID: properties.customSortID ?? currentBin.customSortID,
      binGrpNo: properties.binGrpNo ?? currentBin.binGrpNo,
      // Preserve other fields
      description1: currentBin.description1,
      resourceNo: currentBin.resourceNo,
      pickCustomOrder: currentBin.pickCustomOrder,
      putAwayCustomOrder: currentBin.putAwayCustomOrder,
      stcBinCatNo: currentBin.stcBinCatNo,
      negativeReservationAllowed: currentBin.negativeReservationAllowed,
      sharedBinPreferEmptyBin: currentBin.sharedBinPreferEmptyBin,
      // Tri-state unntaksflagg (TW 36344429) — må bæres videre, ellers nullstilles det til arv
      barCodeConfirmLocation: currentBin.barCodeConfirmLocation,
    };

    return stcBinService.update(companyNo, warehouseID, binCode, request);
  }

  /**
   * Update or create ItemBinSize capacity
   */
  async updateItemCapacity(
    companyNo: string,
    itemNo: string,
    binSizeNo: number,
    existingId: number | null,
    capacity: EditableItemCapacity
  ): Promise<ItemBinSizeDto> {
    if (existingId) {
      // Update existing
      const request: UpdateItemBinSizeRequest = {
        maxQuantity: capacity.maxQuantity,
        putAwayPriorityNo: capacity.putAwayPriorityNo,
      };
      return itemBinSizeService.update(existingId, request);
    } else {
      // Create new
      const request: CreateItemBinSizeRequest = {
        companyNo,
        itemNo,
        binSizeNo,
        maxQuantity: capacity.maxQuantity,
        putAwayPriorityNo: capacity.putAwayPriorityNo,
      };
      return itemBinSizeService.create(request);
    }
  }

  /**
   * Bulk apply capacity to multiple items with the same binSize
   * (Quick-apply "use for similar" feature)
   */
  async bulkApplyCapacity(
    companyNo: string,
    items: FieldRegistrationItem[],
    binSizeNo: number,
    capacity: EditableItemCapacity,
    applyToMissingOnly: boolean
  ): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    const targetItems = applyToMissingOnly
      ? items.filter(i => !i.capacityConfigured)
      : items;

    for (const item of targetItems) {
      try {
        await this.updateItemCapacity(
          companyNo,
          item.itemNo,
          binSizeNo,
          item.itemBinSize?.id || null,
          capacity
        );
        success++;
      } catch (error) {
        console.error(`[FieldRegistration] Failed to apply capacity for ${item.itemNo}:`, error);
        failed++;
      }
    }

    return { success, failed };
  }

  /**
   * Update an item's StdBinCode (fast plukklokasjon)
   */
  async updateItemStdBinCode(
    companyNo: string,
    itemNo: string,
    stdBinCode: string
  ): Promise<ItemsDto> {
    const request: UpdateItemsRequest = {
      stdBinCode: stdBinCode || undefined,
    };
    return itemsService.updateItem(companyNo, itemNo, request);
  }

  // ── Calculation Helpers ──────────────────────────────────────────

  /**
   * Determine which bin fields are overridden from BinType defaults
   * Uses COALESCE logic: Bin value takes precedence, BinType is fallback
   */
  calculateOverrides(bin: StcBinDto, binType: StcBinTypeDto | null): BinFieldOverrides {
    if (!binType) {
      // No bin type = everything is an override (or unknown)
      return {
        pickPriorityNo: bin.pickPriorityNo != null,
        putAwayPriorityNo: bin.putAwayPriorityNo != null,
        sharedBinYesNo: bin.sharedBinYesNo != null,
        autoReplenishment: bin.autoReplenishment != null,
        replenishMinLevel: bin.replenishMinLevel != null,
        replenishMaxLevel: bin.replenishMaxLevel != null,
        pickableYesNo: bin.pickableYesNo != null,
        reservationAllowed: bin.reservationAllowed != null,
      };
    }

    return {
      pickPriorityNo: bin.pickPriorityNo != null && bin.pickPriorityNo !== binType.pickPriorityNo,
      putAwayPriorityNo: bin.putAwayPriorityNo != null && bin.putAwayPriorityNo !== binType.putAwayPriorityNo,
      sharedBinYesNo: bin.sharedBinYesNo != null && bin.sharedBinYesNo !== binType.sharedBinYesNo,
      autoReplenishment: bin.autoReplenishment != null && bin.autoReplenishment !== binType.autoReplenishment,
      replenishMinLevel: bin.replenishMinLevel != null && bin.replenishMinLevel !== binType.replenishMinLevel,
      replenishMaxLevel: bin.replenishMaxLevel != null && bin.replenishMaxLevel !== binType.replenishMaxLevel,
      pickableYesNo: bin.pickableYesNo != null && bin.pickableYesNo !== binType.pickableYesNo,
      reservationAllowed: bin.reservationAllowed != null && bin.reservationAllowed !== binType.reservationAllowed,
    };
  }

  /**
   * Calculate completion status for a location
   */
  calculateCompletionStatus(
    items: FieldRegistrationItem[],
    overrides: BinFieldOverrides
  ): LocationCompletionStatus {
    const totalItems = items.length;
    const itemsWithCapacity = items.filter(i => i.capacityConfigured).length;
    const binPropertiesReviewed = Object.values(overrides).some(v => v);

    // Completion: 50% for bin properties reviewed, 50% for item capacity
    let completionPercent = 0;
    if (totalItems > 0) {
      const capacityPercent = (itemsWithCapacity / totalItems) * 50;
      const propertiesPercent = binPropertiesReviewed ? 50 : 0;
      completionPercent = Math.round(capacityPercent + propertiesPercent);
    } else {
      // No items = only bin properties matter
      completionPercent = binPropertiesReviewed ? 100 : 0;
    }

    return {
      totalItems,
      itemsWithCapacity,
      binPropertiesReviewed,
      completionPercent,
    };
  }

  /**
   * Get the effective (resolved) value for a bin field
   * Implements COALESCE(StcBin.Property, StcBinType.Property)
   */
  getEffectiveValue<T>(binValue: T | null | undefined, typeValue: T | null | undefined): T | null {
    if (binValue != null) return binValue;
    if (typeValue != null) return typeValue;
    return null;
  }

  /**
   * Build summary statistics for a set of locations
   */
  buildSummary(locations: FieldRegistrationLocation[]): FieldRegistrationSummary {
    let totalItems = 0;
    let itemsWithCapacity = 0;
    let itemsMissingCapacity = 0;
    let locationsWithOverrides = 0;
    let locationsUsingDefaults = 0;
    let completedLocations = 0;

    for (const loc of locations) {
      totalItems += loc.completionStatus.totalItems;
      itemsWithCapacity += loc.completionStatus.itemsWithCapacity;
      itemsMissingCapacity += loc.completionStatus.totalItems - loc.completionStatus.itemsWithCapacity;

      if (Object.values(loc.overrides).some(v => v)) {
        locationsWithOverrides++;
      } else {
        locationsUsingDefaults++;
      }

      if (loc.completionStatus.completionPercent >= 100) {
        completedLocations++;
      }
    }

    return {
      totalLocations: locations.length,
      completedLocations,
      totalItems,
      itemsWithCapacity,
      itemsMissingCapacity,
      locationsWithOverrides,
      locationsUsingDefaults,
    };
  }

}

export const fieldRegistrationService = new FieldRegistrationService();
